import { buildCohortFilters, DEFAULT_COHORT, VobListCohort } from './cohorts';
import type { FilterModel } from './filters';

export type LinkFilters = Record<string, string[]>;

export type SortModelItem = { colId: string; sort: 'asc' | 'desc' };

export const DEFAULT_SORT_MODEL: SortModelItem[] = [{ colId: 'verified_at', sort: 'desc' }];

export const VERIFIED_WITHIN_OPTIONS = ['today', '6m', '1y', 'all'] as const;

export type VerifiedWithin = (typeof VERIFIED_WITHIN_OPTIONS)[number];

export const isVerifiedWithin = (value: unknown): value is VerifiedWithin =>
  VERIFIED_WITHIN_OPTIONS.includes(value as VerifiedWithin);

export interface VobListUrlState {
  cohort: VobListCohort;
  memberId?: string;
  columnFilters: FilterModel;
  linkFilters: LinkFilters;
  page: number;
  sortModel: SortModelItem[];
  verifiedWithin: VerifiedWithin;
}

type WritableUrlState = Omit<
  VobListUrlState,
  'memberId' | 'columnFilters' | 'page' | 'sortModel' | 'verifiedWithin'
> &
  Partial<Pick<VobListUrlState, 'columnFilters' | 'page' | 'sortModel' | 'verifiedWithin'>>;

export const VOB_LIST_URL_KEYS = [
  'member_id',
  'filters',
  'verified_within',
  'is_most_recent_per_policy',
  'is_treatable',
  'is_active_member',
  'is_active_policy',
  'include_deleted_policies',
  'include_excluded_from_analytics_members',
  'vob_stale_days'
];

export const PICKER_KEYS = ['policy_carrier', 'policy_plan', 'member_jurisdiction'];

export const LINK_FILTER_KEYS = [
  'member_id_in',
  'member_name',
  'member_name_in',
  'member_first_name',
  'member_first_name_in',
  'member_last_name',
  'member_last_name_in',
  'member_jurisdiction',
  'member_jurisdiction_in',
  'member_status',
  'member_status_in',
  'member_birthday',
  'member_birthday_from',
  'member_birthday_to',
  'policy_id',
  'policy_id_in',
  'policy_carrier',
  'policy_carrier_in',
  'policy_plan',
  'policy_plan_in',
  'policy_priority_type',
  'policy_priority_type_in',
  'policy_provider_name',
  'policy_provider_name_in',
  'policy_created_at',
  'policy_created_at_from',
  'policy_created_at_to',
  'policy_member_id',
  'policy_member_id_in',
  'policy_subscriber_id',
  'policy_subscriber_id_in',
  'policy_group_id',
  'policy_group_id_in',
  'policy_verification_status',
  'policy_verification_status_in',
  'policy_type',
  'policy_type_in',
  'policy_start_date_from',
  'policy_start_date_to',
  'policy_end_date_from',
  'policy_end_date_to',
  'verification_id',
  'verification_id_in',
  'status',
  'status_in',
  'verified_via',
  'verified_via_in',
  'service_code',
  'service_code_in',
  'verified_by',
  'verified_by_in',
  'verified_at',
  'verified_at_from',
  'verified_at_to',
  'verified_through',
  'verified_through_from',
  'verified_through_to',
  'created_at',
  'created_at_from',
  'created_at_to',
  'external_verification_state',
  'external_verification_state_in',
  'external_verification_source',
  'external_verification_source_in',
  'external_name',
  'eligibility_status'
];

const DEFAULT_ON_URL_KEYS = ['is_most_recent_per_policy', 'is_active_member', 'is_active_policy'];

const NARROWING_LINK_KEYS = [
  'member_id_in',
  'policy_id',
  'policy_id_in',
  'verification_id',
  'verification_id_in',
  'verified_at',
  'verified_at_from',
  'verified_at_to'
];

const toYMD = (date: Date): string =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');

export const verifiedWithinStart = (
  verifiedWithin: VerifiedWithin,
  today: Date = new Date()
): string | undefined => {
  if (verifiedWithin === 'all') return undefined;

  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (verifiedWithin === '6m') start.setMonth(start.getMonth() - 6);
  if (verifiedWithin === '1y') start.setFullYear(start.getFullYear() - 1);
  return toYMD(start);
};

const readBoolean = (params: URLSearchParams, key: string): boolean => params.get(key) === 'true';

const hasAnyVobListParam = (params: URLSearchParams): boolean =>
  [...VOB_LIST_URL_KEYS, ...LINK_FILTER_KEYS].some((key) => params.has(key));

const readPositiveInt = (params: URLSearchParams, key: string): number | undefined => {
  const raw = params.get(key);
  if (!raw) return undefined;

  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const readCohort = (params: URLSearchParams): VobListCohort => ({
  isMostRecentPerPolicy:
    readBoolean(params, 'is_most_recent_per_policy') ||
    (params.has('policy_id_in') && !params.has('is_most_recent_per_policy')),
  isTreatable: readBoolean(params, 'is_treatable'),
  isActiveMember: readBoolean(params, 'is_active_member'),
  isActivePolicy: readBoolean(params, 'is_active_policy'),
  includeDeletedPolicies: readBoolean(params, 'include_deleted_policies'),
  includeExcludedFromAnalyticsMembers: readBoolean(
    params,
    'include_excluded_from_analytics_members'
  ),
  staleDays: readPositiveInt(params, 'vob_stale_days')
});

const sortParamOf = (sortModel: SortModelItem[]): string =>
  sortModel.map(({ colId, sort }) => `${sort === 'desc' ? '-' : ''}${colId}`).join(',');

const readSortModel = (params: URLSearchParams): SortModelItem[] => {
  const tokens = (params.get('sort') ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);
  if (!tokens.length) return DEFAULT_SORT_MODEL;

  return tokens.map((token) => ({
    colId: token.replace(/^-/, ''),
    sort: token.startsWith('-') ? 'desc' : 'asc'
  }));
};

const readColumnFilters = (params: URLSearchParams): FilterModel => {
  let columnFilters: FilterModel = {};
  try {
    const parsed = JSON.parse(params.get('filters') ?? '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) columnFilters = parsed;
  } catch {
    columnFilters = {};
  }

  const legacyMemberId = params.get('member_id');
  if (legacyMemberId && !columnFilters.member_id) {
    columnFilters = {
      ...columnFilters,
      member_id: { filterType: 'text', type: 'equals', filter: legacyMemberId }
    };
  }

  return columnFilters;
};

const memberIdOf = (columnFilters: FilterModel): string | undefined => {
  const memberFilter = columnFilters.member_id;
  return memberFilter?.type === 'equals' && memberFilter.filter ? memberFilter.filter : undefined;
};

const readLinkFilters = (params: URLSearchParams): LinkFilters =>
  LINK_FILTER_KEYS.reduce<LinkFilters>((filters, key) => {
    const values = params.getAll(key).filter(Boolean);
    return values.length ? { ...filters, [key]: values } : filters;
  }, {});

export const readVobListUrlState = (params: URLSearchParams): VobListUrlState => {
  const columnFilters = readColumnFilters(params);
  const state = {
    cohort: hasAnyVobListParam(params) ? readCohort(params) : DEFAULT_COHORT,
    memberId: memberIdOf(columnFilters),
    columnFilters,
    linkFilters: readLinkFilters(params),
    page: readPositiveInt(params, 'page') ?? 1,
    sortModel: readSortModel(params)
  };
  const requested = params.get('verified_within');

  return {
    ...state,
    verifiedWithin: isVerifiedWithin(requested) ? requested : isNarrowUrlState(state) ? 'all' : '6m'
  };
};

export const writeVobListUrlState = (
  previous: URLSearchParams,
  state: WritableUrlState
): URLSearchParams => {
  const next = new URLSearchParams(previous);
  [...VOB_LIST_URL_KEYS, ...LINK_FILTER_KEYS, 'page', 'sort'].forEach((key) => next.delete(key));

  Object.entries(buildCohortFilters(state.cohort)).forEach(([key, value]) => {
    next.set(key, value);
  });
  DEFAULT_ON_URL_KEYS.forEach((key) => {
    if (!next.has(key)) next.set(key, 'false');
  });

  if (state.columnFilters && Object.keys(state.columnFilters).length > 0) {
    next.set('filters', JSON.stringify(state.columnFilters));
  }
  if (state.verifiedWithin) next.set('verified_within', state.verifiedWithin);
  Object.entries(state.linkFilters).forEach(([key, values]) => {
    values.forEach((value) => next.append(key, value));
  });

  if (state.page && state.page > 1) next.set('page', String(state.page));
  const sort = sortParamOf(state.sortModel ?? DEFAULT_SORT_MODEL);
  if (sort && sort !== sortParamOf(DEFAULT_SORT_MODEL)) next.set('sort', sort);

  return next;
};

export const buildRequestFilters = ({
  cohort,
  linkFilters,
  verifiedWithin = 'all'
}: Pick<VobListUrlState, 'cohort' | 'linkFilters'> &
  Partial<Pick<VobListUrlState, 'verifiedWithin'>>): Record<string, string | string[]> => {
  const verifiedFrom = verifiedWithinStart(verifiedWithin);
  const filters: Record<string, string | string[]> = {
    ...(verifiedFrom && { verified_at_from: verifiedFrom }),
    ...buildCohortFilters(cohort)
  };
  Object.entries(linkFilters).forEach(([key, values]) => {
    filters[key] = values.length === 1 ? values[0] : values;
  });

  return filters;
};

export const isNarrowUrlState = (
  state: Pick<VobListUrlState, 'cohort' | 'memberId' | 'linkFilters'>
): boolean =>
  Boolean(state.memberId) ||
  NARROWING_LINK_KEYS.some((key) => key in state.linkFilters) ||
  state.cohort.staleDays != null;
